import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { gql } from "graphql-request";
import { graphqlClient } from "@/lib/graphql";
import type { Notification } from "@/types";
import { LoadingSpinner } from "@/components/ui/LoadingSpinner";
import { EmptyState } from "@/components/ui/EmptyState";

const NOTIFICATIONS_QUERY = gql`
  query Notifications {
    notifications {
      id
      eventType
      title
      body
      read
      notifiableType
      notifiableId
      icon
      createdAt
    }
  }
`;

const MARK_ALL_READ_MUTATION = gql`
  mutation MarkAllNotificationsRead {
    markAllNotificationsRead {
      success
    }
  }
`;

const MARK_READ_MUTATION = gql`
  mutation MarkNotificationRead($id: ID!) {
    markNotificationRead(id: $id) {
      notification {
        id
        read
      }
      errors
    }
  }
`;

function timeAgo(dateStr: string): string {
  const now = new Date();
  const date = new Date(dateStr);
  const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (seconds < 60) return "ką tik";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `prieš ${minutes} min.`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `prieš ${hours} val.`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `prieš ${days} d.`;
  return date.toLocaleDateString("lt-LT");
}

const ICON_MAP: Record<string, string> = {
  message: "M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z",
  listing: "M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10",
  wishlist: "M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z",
  purchase: "M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 100 4 2 2 0 000-4z",
  default: "M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9",
};

function getIconPath(icon: string): string {
  return ICON_MAP[icon] || ICON_MAP.default;
}

export function NotificationsPage() {
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["notifications"],
    queryFn: () =>
      graphqlClient.request<{ notifications: Notification[] }>(
        NOTIFICATIONS_QUERY
      ),
  });

  const markAllReadMutation = useMutation({
    mutationFn: () =>
      graphqlClient.request<{ markAllNotificationsRead: { success: boolean } }>(
        MARK_ALL_READ_MUTATION
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notifications"] });
      queryClient.invalidateQueries({ queryKey: ["unreadCounts"] });
    },
  });

  const markReadMutation = useMutation({
    mutationFn: (id: string) =>
      graphqlClient.request<{
        markNotificationRead: { notification: Notification | null; errors: string[] };
      }>(MARK_READ_MUTATION, { id }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notifications"] });
      queryClient.invalidateQueries({ queryKey: ["unreadCounts"] });
    },
  });

  const notifications = data?.notifications || [];
  const hasUnread = notifications.some((n) => !n.read);

  return (
    <div className="mx-auto max-w-3xl px-4 py-8 sm:px-6 lg:px-8">
      <div className="mb-8 flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Pranešimai</h1>
        {hasUnread && (
          <button
            onClick={() => markAllReadMutation.mutate()}
            disabled={markAllReadMutation.isPending}
            className="text-sm font-medium text-primary-800 hover:text-primary-700"
          >
            {markAllReadMutation.isPending
              ? "Žymima..."
              : "Pažymėti visus kaip skaitytus"}
          </button>
        )}
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <LoadingSpinner />
        </div>
      ) : notifications.length === 0 ? (
        <EmptyState
          title="Pranešimų nėra"
          description="Neturite jokių pranešimų."
          icon={
            <svg className="h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
            </svg>
          }
        />
      ) : (
        <div className="divide-y divide-gray-100 overflow-hidden rounded-xl border border-gray-200 bg-white">
          {notifications.map((notification) => (
            <div
              key={notification.id}
              className={`flex items-start gap-4 p-4 ${
                !notification.read ? "bg-blue-50/50" : ""
              }`}
            >
              {/* Icon */}
              <div
                className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${
                  !notification.read ? "bg-primary-100 text-primary-800" : "bg-gray-100 text-gray-500"
                }`}
              >
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d={getIconPath(notification.icon)}
                  />
                </svg>
              </div>

              {/* Content */}
              <div className="min-w-0 flex-1">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-2">
                    {!notification.read && (
                      <span className="inline-block h-2 w-2 shrink-0 rounded-full bg-blue-500" />
                    )}
                    <p className={`text-sm ${!notification.read ? "font-semibold text-gray-900" : "font-medium text-gray-900"}`}>
                      {notification.title}
                    </p>
                  </div>
                  <span className="shrink-0 text-xs text-gray-500">
                    {timeAgo(notification.createdAt)}
                  </span>
                </div>
                {notification.body && (
                  <p className="mt-0.5 text-sm text-gray-500">
                    {notification.body}
                  </p>
                )}
              </div>

              {/* Read toggle */}
              <button
                onClick={() => markReadMutation.mutate(notification.id)}
                disabled={markReadMutation.isPending}
                className="shrink-0 rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
                title={notification.read ? "Pažymėti kaip neskaitytą" : "Pažymėti kaip skaitytą"}
              >
                {notification.read ? (
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 19v-8.93a2 2 0 01.89-1.664l7-4.666a2 2 0 012.22 0l7 4.666A2 2 0 0121 10.07V19M3 19a2 2 0 002 2h14a2 2 0 002-2M3 19l6.75-4.5M21 19l-6.75-4.5M3 10l6.75 4.5M21 10l-6.75 4.5m0 0l-1.14.76a2 2 0 01-2.22 0l-1.14-.76" />
                  </svg>
                ) : (
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
